import { useNavigation } from 'components/navigation/NavigationContext';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useModal } from 'components/modal/ModalContext';
import { SLUGS } from 'helpers/consts';
import StaticPage from './StaticPage';

type ModalPageProps = {
  formComponent: React.ComponentType<any>;
  formComponentProps?: Record<string, any>;
  backdropSlug?: string; // Slug for backdrop content (e.g., 'welcome')
  navigateOnClose?: string; // Where to navigate when modal closes (if undefined, doesn't auto-navigate)
  className?: string;
};

/**
 * ModalPage: A route component that opens a form in a modal
 *
 * For restricted routes, authentication is enforced by the route loader,
 * so this component can safely render the modal form immediately.
 *
 * Features:
 * - Shows a backdrop page (e.g., /welcome) behind the modal
 * - Forms can navigate directly - ModalPage detects URL changes and doesn't interfere
 * - Automatically navigates to default destination if modal closes without navigation
 */
export default function ModalPage(props: ModalPageProps) {
  const {
    formComponent: FormComponent,
    formComponentProps,
    backdropSlug = SLUGS.FRONT,
    navigateOnClose,
    className,
  } = props;

  const { openModal, isModalOpen } = useModal();
  const location = useLocation();
  const { navigate, navigationInProgress } = useNavigation();
  const [initialPath, setInitialPath] = useState('');
  const modalOpenedRef = useRef(false);

  // Open the modal when component mounts or when modal becomes empty (e.g., after LoginForm closes)
  useEffect(() => {
    // If another modal is open or our modal was opened before, do not re-open it
    if (isModalOpen || modalOpenedRef.current) {
      return;
    }
    modalOpenedRef.current = true;
    setInitialPath(location.pathname);
    openModal(<FormComponent {...formComponentProps} />);
  }, [
    FormComponent,
    formComponentProps,
    location.pathname,
    openModal,
    isModalOpen,
  ]);

  // Handle what happens when modal closes
  useEffect(() => {
    if (!isModalOpen && modalOpenedRef.current) {
      // Only navigate if navigateOnClose is specified and we haven't navigated away
      if (
        navigateOnClose &&
        !navigationInProgress() &&
        location.pathname === initialPath
      ) {
        setTimeout(() => {
          navigate(navigateOnClose);
        }, 100);
      }
    }
  }, [
    isModalOpen,
    navigate,
    navigateOnClose,
    location.pathname,
    initialPath,
    navigationInProgress,
  ]);

  // Render backdrop page while modal is open
  if (!FormComponent) {
    console.error('ModalPage: FormComponent is not provided');
  }

  return <StaticPage slug={backdropSlug} className={className} />;
}
